import { FieldShell } from '@atlas/ui-components'
import { Button, Combobox, type ComboboxOption } from '@atlas/ui-primitives'
import { useId } from 'react'

export interface CampaignFormValues {
	name: string
	windowLabel: string
	description: string
	veevaCode: string
	objectiveCode: string | null
	geographyCode: string | null
	brandCode: string | null
	/** Independent of `brandCode`: neither defaults, clears, or constrains the other. */
	productCode: string | null
}

export type CampaignFormFieldErrors = Partial<Record<keyof CampaignFormValues, string>>

export interface CampaignFormProps {
	values: CampaignFormValues
	fieldErrors: CampaignFormFieldErrors
	isDirty: boolean
	isPending: boolean
	generalError?: string
	isSuccess?: boolean
	/** Present and read-only on Edit; absent on New. */
	campaignCode?: string
	objectiveOptions: readonly ComboboxOption[]
	geographyOptions: readonly ComboboxOption[]
	brandOptions: readonly ComboboxOption[]
	/** Every promoted product, flat: the brand relation is not on the `product` axis. */
	productOptions: readonly ComboboxOption[]
	onFieldChange: <K extends keyof CampaignFormValues>(field: K, value: CampaignFormValues[K]) => void
	onSubmit: () => void
	onCancel: () => void
}

/** A Combobox in a design-system field cell, associated by `aria-labelledby` — never an unrelated `aria-label`. */
function LabeledCombobox({
	label,
	options,
	value,
	onChange,
}: {
	label: string
	options: readonly ComboboxOption[]
	value: string | null
	onChange: (value: string | undefined) => void
}) {
	const labelId = useId()
	return (
		<FieldShell label={label} labelId={labelId}>
			<Combobox options={[...options]} value={value} onChange={onChange} aria-labelledby={labelId} isClearable />
		</FieldShell>
	)
}

interface LabeledTextFieldProps {
	label: string
	value: string
	onChange: (value: string) => void
	error?: string
	multiline?: boolean
}

/** A text input or textarea in a design-system field cell, with an optional inline field error. */
function LabeledTextField({ label, value, onChange, error, multiline }: LabeledTextFieldProps) {
	const inputId = useId()
	const errorId = `${inputId}-error`
	const inputClassName =
		'w-full rounded-ds-md border border-ds-border bg-ds-surface-elevated px-3 py-2 text-ds-sm text-ds-text'
	return (
		<FieldShell label={label} htmlFor={inputId} error={error}>
			{multiline ? (
				<textarea
					id={inputId}
					value={value}
					onChange={(event) => onChange(event.target.value)}
					className={inputClassName}
				/>
			) : (
				<input
					id={inputId}
					type="text"
					value={value}
					onChange={(event) => onChange(event.target.value)}
					className={inputClassName}
					aria-invalid={error !== undefined}
					aria-describedby={error ? errorId : undefined}
				/>
			)}
		</FieldShell>
	)
}

/** Which prop supplies each Classification control's options, in the order they render. */
const CLASSIFICATION_FIELDS = [
	{ label: 'Objective', field: 'objectiveCode', optionsKey: 'objectiveOptions' },
	{ label: 'Geography', field: 'geographyCode', optionsKey: 'geographyOptions' },
	{ label: 'Brand', field: 'brandCode', optionsKey: 'brandOptions' },
	{ label: 'Product', field: 'productCode', optionsKey: 'productOptions' },
] as const satisfies readonly {
	label: string
	field: keyof CampaignFormValues
	optionsKey: keyof CampaignFormProps
}[]

/**
 * The Classification block: one optional single-select per axis the form offers, each
 * independent of the others — selecting a Brand neither filters nor presets Product.
 *
 * Its own component so the form stays inside the frontend function-length budget, and
 * because these four controls are one cohesive group rather than four unrelated fields.
 */
function ClassificationFields({ values, onFieldChange, ...options }: CampaignFormProps) {
	return (
		<>
			{CLASSIFICATION_FIELDS.map(({ label, field, optionsKey }) => (
				<LabeledCombobox
					key={field}
					label={label}
					options={options[optionsKey] as readonly ComboboxOption[]}
					value={values[field] as string | null}
					onChange={(value) => onFieldChange(field, (value ?? null) as CampaignFormValues[typeof field])}
				/>
			))}
		</>
	)
}

/**
 * The shared Campaign create/edit form. Receives field values, validation and
 * submission state, Classification option lists, and callbacks — it imports no
 * Classification hook or fixture and renders no campaign-code or status input.
 * Campaign code (Edit only) is displayed read-only, never editable.
 */
export function CampaignForm(props: CampaignFormProps) {
	const {
		values,
		fieldErrors,
		isDirty,
		isPending,
		generalError,
		isSuccess,
		campaignCode,
		onFieldChange,
		onSubmit,
		onCancel,
	} = props
	return (
		<form
			onSubmit={(event) => {
				event.preventDefault()
				onSubmit()
			}}
			className="space-y-5"
		>
			{campaignCode !== undefined && (
				<div className="space-y-1.5">
					<span className="block text-sm font-medium text-ds-text">Campaign code</span>
					<p className="text-sm text-ds-text-muted">{campaignCode}</p>
				</div>
			)}

			<LabeledTextField
				label="Name"
				value={values.name}
				onChange={(value) => onFieldChange('name', value)}
				error={fieldErrors.name}
			/>
			<LabeledTextField
				label="Window"
				value={values.windowLabel}
				onChange={(value) => onFieldChange('windowLabel', value)}
			/>
			<LabeledTextField
				label="Description"
				value={values.description}
				onChange={(value) => onFieldChange('description', value)}
				multiline
			/>
			<LabeledTextField
				label="Veeva code"
				value={values.veevaCode}
				onChange={(value) => onFieldChange('veevaCode', value)}
				error={fieldErrors.veevaCode}
			/>

			<ClassificationFields {...props} />

			{generalError && (
				<p role="alert" className="text-sm text-ds-danger">
					{generalError}
				</p>
			)}
			{isSuccess && (
				<p role="status" className="text-sm text-ds-success">
					Saved.
				</p>
			)}

			<div className="flex justify-end gap-3">
				<Button variant="outline" onPress={onCancel}>
					Cancel
				</Button>
				<Button type="submit" isDisabled={!isDirty || isPending}>
					{isPending ? 'Saving…' : 'Save'}
				</Button>
			</div>
		</form>
	)
}
